import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import type { AlisonRunCopy, WorkflowDetail } from '../workflows.types.js';
import type { AlisonSetup } from './data/alisonLive.js';
import {
  type AlisonActionDisplay,
  type AlisonRow,
  type AlisonSession,
  builtRows,
  type ChainCard,
  chainPath,
  isFinished,
  type PillState,
  pillState,
  reachableActions,
  routeParams,
  runRows,
} from './data/alisonRun.js';
import { mockSession } from './data/alisonRunMock.js';
import { playDoneChime } from './data/chime.js';
import type { AlisonLiveRun } from './data/useAlisonLiveRun.js';

export const ALISON_TICK_MS = 3000;

// Null until the catalog's copy arrives: nothing of Run with Alison renders before it.
const AlisonRunContext = createContext<AlisonRunValue | null>(null);
// Pages register through this one, so registering is what fetches the catalog and its copy.
const AlisonEntryContext = createContext<AlisonEntryValue | null>(null);

export interface AlisonForm {
  actionType: string;
  params: Record<string, unknown>;
  parent?: { runId: string; selectItems: Record<string, unknown>[] };
  // Labels of unset required fields, and sentences that block the run on their own.
  missing: string[];
  blockers: string[];
}

export interface AlisonRunValue {
  copy: AlisonRunCopy;
  setup: AlisonSetup | undefined;
  details: WorkflowDetail[];
  // False while a live run has no form to start from: the step is loading, or already ran.
  formReady: boolean;
  missing: string[];
  blockers: string[];
  displays: Record<string, AlisonActionDisplay>;
  path: string[] | null;
  // Every catalog step with the service's edges, for building a path from `path[0]`.
  cards: ChainCard[];
  session: AlisonSession | null;
  rows: AlisonRow[];
  pill: PillState | null;
  introOpen: boolean;
  drawerOpen: boolean;
  resultsOpen: boolean;
  peekActionType: string | null;
  // The step whose settings are open for a rerun.
  editingActionType: string | null;
  register: (
    workflowId: string,
    goal: string | null,
    setup?: AlisonSetup,
    search?: Record<string, unknown>,
  ) => void;
  unregister: (workflowId: string) => void;
  openIntro: () => void;
  closeIntro: () => void;
  begin: () => void;
  start: (
    stopAt: number,
    selectedPath?: string[],
    paramsByAction?: AlisonSetup['paramsByAction'],
  ) => void;
  stop: () => void;
  openDrawer: () => void;
  closeDrawer: () => void;
  peek: (actionType: string | null) => void;
  editStep: (actionType: string | null) => void;
  paramsOf: (actionType: string) => Record<string, unknown> | null;
  // Only the params the user changed; the rest keeps following the step before.
  rerunFrom: (actionType: string, changes: Record<string, unknown>) => void;
  openResults: () => void;
  closeResults: () => void;
}

interface AlisonEntryValue {
  register: AlisonRunValue['register'];
  setForm: (form: AlisonForm | null) => void;
  unregister: AlisonRunValue['unregister'];
  resultsWorkflowId: string | null;
  // The run the finished path ended on, so its page reopens there.
  resultsRunId: string | null;
}

interface Entry {
  workflowId: string;
  goal: string | null;
  setup: AlisonSetup | undefined;
  search: Record<string, unknown> | undefined;
}

interface MockRun {
  // The page the run started on, which is not path[0] when it starts from a chained step.
  workflowId: string;
  path: string[];
  stopAt: number;
  tick: number;
  // The root page's URL search when the run started, so results reopen on that scope.
  search?: Record<string, unknown> | undefined;
}

interface AlisonRunSeed extends Omit<MockRun, 'workflowId'> {
  drawerOpen: boolean;
  resultsOpen?: boolean;
}

interface AlisonRunProviderProps {
  details?: WorkflowDetail[];
  children: ReactNode;
  cards: ChainCard[];
  displays: Record<string, AlisonActionDisplay>;
  copy: AlisonRunCopy | null;
  seed?: AlisonRunSeed | undefined;
  // Absent in stories and tests, which run the mock.
  live?: AlisonLiveRun | undefined;
  onOpenResults?: (
    workflowId: string,
    search: Record<string, unknown> | undefined,
    runId: string | null,
  ) => void;
  onPathActions?: (actions: string[]) => void;
  onActiveChange?: (active: boolean) => void;
}

export function AlisonRunProvider({
  children,
  details = [],
  cards,
  displays,
  copy,
  seed,
  live,
  onOpenResults,
  onPathActions,
  onActiveChange,
}: AlisonRunProviderProps): ReactNode {
  const [entry, setEntry] = useState<Entry | null>(null);
  const [form, setFormState] = useState<AlisonForm | null>(null);
  const setForm = useCallback((next: AlisonForm | null) => setFormState(next), []);
  const [run, setRun] = useState<MockRun | null>(
    seed === undefined
      ? null
      : { workflowId: seed.path[0] ?? '', path: seed.path, stopAt: seed.stopAt, tick: seed.tick },
  );
  const [introOpen, setIntroOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(seed?.drawerOpen ?? false);
  const [resultsOpen, setResultsOpen] = useState(seed?.resultsOpen ?? false);
  const [peekActionType, setPeekActionType] = useState<string | null>(null);
  const [editingActionType, setEditingActionType] = useState<string | null>(null);

  const fullPath =
    entry === null ? null : chainPath(cards, form?.actionType ?? entry.workflowId, entry.goal);
  const entryPath =
    fullPath?.every((actionType) => displays[actionType] !== undefined) === true ? fullPath : null;
  // A live run's own path: a step's result can re-route Alison's way on mid-run.
  const plan = live?.plan ?? run;
  const path = plan?.path ?? entryPath;
  const session =
    live !== undefined
      ? live.session
      : run === null
        ? null
        : mockSession(run.path, run.stopAt, run.tick, displays);
  const finished = isFinished(session);
  const ended = finished || session?.status === 'failed';
  const rowPath = plan === null ? path : plan.path.slice(0, plan.stopAt + 1);
  const progressOf = (actionType: string): string | null =>
    live?.runOf(actionType)?.progress?.label || null;
  const rows = rowPath === null ? [] : runRows(rowPath, session, displays, progressOf);
  const requestedActions = [
    ...new Set(
      plan?.path ??
        (entry === null
          ? []
          : [
              ...(fullPath ?? []),
              ...reachableActions(cards, form?.actionType ?? entry.workflowId),
            ]),
    ),
  ].join(',');
  const ticking = live === undefined && run !== null && !finished;
  const noPath = path === null;
  const active = entry !== null || run !== null;

  useEffect(() => {
    if (noPath) {
      setIntroOpen(false);
      setDrawerOpen(false);
    }
  }, [noPath]);

  useEffect(() => {
    onActiveChange?.(active);
  }, [active, onActiveChange]);

  useEffect(() => {
    onPathActions?.(requestedActions === '' ? [] : requestedActions.split(','));
  }, [requestedActions, onPathActions]);

  useEffect(() => {
    if (!ticking) return undefined;
    const interval = setInterval(() => {
      setRun((current) => (current === null ? null : { ...current, tick: current.tick + 1 }));
    }, ALISON_TICK_MS);
    return () => clearInterval(interval);
  }, [ticking]);

  const drawerOpenRef = useRef(drawerOpen);
  drawerOpenRef.current = drawerOpen;
  useEffect(() => {
    if (finished && !drawerOpenRef.current) playDoneChime();
  }, [finished]);

  const endedRunOfRef = useRef<string | null>(null);
  endedRunOfRef.current = ended ? (run?.workflowId ?? null) : null;
  const pendingReset = useRef<ReturnType<typeof setTimeout> | null>(null);
  const liveStop = useRef(live?.stop);
  liveStop.current = live?.stop;

  const register = useCallback(
    (
      workflowId: string,
      goal: string | null,
      setup?: AlisonSetup,
      search?: Record<string, unknown>,
    ) => {
      if (pendingReset.current !== null && endedRunOfRef.current === workflowId) {
        clearTimeout(pendingReset.current);
        pendingReset.current = null;
      }
      const next = { workflowId, goal, setup, search };
      setEntry((current) => (JSON.stringify(current) === JSON.stringify(next) ? current : next));
    },
    [],
  );
  const unregister = useCallback((workflowId: string) => {
    setEntry((current) => (current?.workflowId === workflowId ? null : current));
    if (endedRunOfRef.current !== workflowId) return;
    pendingReset.current = setTimeout(() => {
      pendingReset.current = null;
      setRun(null);
      liveStop.current?.();
      setPeekActionType(null);
      setEditingActionType(null);
      setResultsOpen(false);
      setIntroOpen(false);
      setDrawerOpen(false);
    }, 0);
  }, []);

  const clearRun = (): void => {
    setRun(null);
    live?.stop();
    setPeekActionType(null);
    setEditingActionType(null);
    setResultsOpen(false);
  };
  const lastRunId = builtRows(rows).at(-1)?.runId ?? null;
  const openResults = (): void => {
    setDrawerOpen(false);
    setPeekActionType(null);
    setResultsOpen(true);
    if (run !== null) onOpenResults?.(run.workflowId, run.search, lastRunId);
  };

  const value: AlisonRunValue | null =
    copy === null
      ? null
      : {
          copy,
          setup:
            entry?.setup === undefined
              ? undefined
              : { ...entry.setup, ...(form === null ? {} : { params: form.params }) },
          details,
          formReady: live === undefined || form !== null,
          missing: form?.missing ?? [],
          blockers: form?.blockers ?? [],
          displays,
          path,
          cards,
          session,
          rows,
          pill: pillState(path, session, rows),
          introOpen,
          drawerOpen,
          resultsOpen,
          peekActionType,
          editingActionType,
          register,
          unregister,
          openIntro: () => setIntroOpen(true),
          closeIntro: () => setIntroOpen(false),
          begin: () => {
            setIntroOpen(false);
            setDrawerOpen(true);
          },
          start: (stopAt, selectedPath, paramsByAction) => {
            const pathToRun = selectedPath ?? entryPath;
            if (pathToRun?.every((actionType) => displays[actionType] !== undefined) !== true) {
              return;
            }
            if (live !== undefined) {
              if (
                entry?.setup === undefined ||
                form === null ||
                form.missing.length > 0 ||
                form.blockers.length > 0
              ) {
                return;
              }
              live.start(pathToRun, stopAt, {
                ...entry.setup,
                // Only fills what is unset. A chained start inherits its type from the parent run.
                params:
                  form.parent === undefined
                    ? { ...routeParams(cards, pathToRun), ...form.params }
                    : form.params,
                ...(form.parent === undefined ? {} : { parent: form.parent }),
                ...(paramsByAction === undefined ? {} : { paramsByAction }),
              });
            }
            setRun({
              workflowId: entry?.workflowId ?? pathToRun[0] ?? '',
              path: pathToRun,
              stopAt,
              tick: 0,
              search: entry?.search,
            });
          },
          stop: () => {
            clearRun();
            setDrawerOpen(false);
          },
          openDrawer: () => setDrawerOpen(true),
          // Closing a finished run lands it on the page like View does; otherwise the run is lost.
          closeDrawer: () => {
            if (finished) {
              openResults();
              return;
            }
            setDrawerOpen(false);
            if (ended) clearRun();
          },
          peek: setPeekActionType,
          editStep: setEditingActionType,
          paramsOf: (actionType) => live?.paramsOf(actionType) ?? null,
          rerunFrom: (actionType, changes) => {
            const index = plan?.path.indexOf(actionType) ?? -1;
            if (run === null || index < 0) return;
            setEditingActionType(null);
            setPeekActionType(null);
            setResultsOpen(false);
            if (live !== undefined) live.restartFrom(index, changes);
            else setRun({ ...run, tick: Math.min(run.tick, index) });
          },
          openResults,
          closeResults: clearRun,
        };

  const entryValue: AlisonEntryValue = {
    register,
    setForm,
    unregister,
    resultsWorkflowId:
      value !== null && resultsOpen ? (run?.workflowId ?? entry?.workflowId ?? null) : null,
    resultsRunId: value !== null && resultsOpen ? lastRunId : null,
  };

  return (
    <AlisonEntryContext.Provider value={entryValue}>
      <AlisonRunContext.Provider value={value}>{children}</AlisonRunContext.Provider>
    </AlisonEntryContext.Provider>
  );
}

export function useAlisonRunReady(): boolean {
  return useContext(AlisonRunContext) !== null;
}

export function useAlisonRun(): AlisonRunValue {
  const value = useContext(AlisonRunContext);
  if (value === null) throw new Error('useAlisonRun must be used inside AlisonRunProvider');
  return value;
}

// The run a finished path ended on while its completion banner shows on `workflowId`.
export function useAlisonResultsRun(workflowId: string): string | null {
  const value = useContext(AlisonEntryContext);
  return value?.resultsWorkflowId === workflowId ? value.resultsRunId : null;
}

export function useAlisonResultsOpen(workflowId: string): boolean {
  return useContext(AlisonEntryContext)?.resultsWorkflowId === workflowId;
}

export function useAlisonEntry(
  workflowId: string,
  goal: string | null,
  setup?: AlisonSetup,
  search?: Record<string, unknown>,
  enabled = true,
): void {
  const value = useContext(AlisonEntryContext);
  const register = value?.register;
  const unregister = value?.unregister;
  useEffect(() => {
    if (enabled) register?.(workflowId, goal, setup, search);
    else unregister?.(workflowId);
  }, [register, unregister, enabled, workflowId, goal, setup, search]);
  useEffect(() => {
    return () => unregister?.(workflowId);
  }, [unregister, workflowId]);
}

// Null while the step has no form to offer: Run with Alison then waits instead of
// starting the step a second time.
export function useAlisonForm(form: AlisonForm | null): void {
  const setForm = useContext(AlisonEntryContext)?.setForm;
  const key = form === null ? null : JSON.stringify(form);
  useEffect(() => {
    setForm?.(key === null ? null : (JSON.parse(key) as AlisonForm));
  }, [setForm, key]);
  useEffect(() => () => setForm?.(null), [setForm]);
}
